// src/pages/admin/Products.tsx
//
// Admin: products CRUD. Lists every product in the catalog with
// its gallery, lets the admin create, edit, retag, retune the
// pricing/featured/in-stock flags, and re-order the image
// gallery. Also lets the admin promote drafts from the approval
// queue into the live catalog.
//
// Approval-flow: a product's `approved` flag is set to:
//   - "approved": visible in the public catalog (default for new
//     manual edits)
//   - "pending":  in /admin/approvals queue; the public catalog hides it
//   - "rejected": archived; needs manual un-reject
//
// Day 5 polish. Future (Week 5+): the catalog-builder agent writes
// drafts that surface here as `pending`. Until then this admin can
// hand-create drafts and approve them.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Trash2, Check, X, Image as ImageIcon, GripVertical } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { EmptyState } from "@/components/ui/EmptyState";
import { DEMO_PRODUCTS, type Product, type Category } from "@/lib/demoData";
import { formatPrice } from "@/lib/formatPrice";
import { cn } from "@/lib/cn";

const CATEGORIES: Category[] = ["video", "audio", "screens", "acoustic", "accessories"];

const APPROVED_OPTIONS: Array<{ value: Product["approved"]; labelKey: string }> = [
  { value: "approved", labelKey: "admin.products.approvedOption" },
  { value: "pending", labelKey: "admin.products.pendingOption" },
  { value: "rejected", labelKey: "admin.products.rejectedOption" },
];

/** Label key for the approval badge; the enum value itself is untouched. */
const APPROVED_STATUS_LABEL_KEYS: Record<Product["approved"], string> = {
  approved: "admin.products.statusApproved",
  pending: "admin.products.statusPending",
  rejected: "admin.products.statusRejected",
};

export function AdminProducts() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>(DEMO_PRODUCTS);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  // Honor deep-link `?edit=SKU` so the product detail page can
  // jump straight into the editor for a specific item.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const editSku = params.get("edit");
    if (editSku && products.some((p) => p.sku === editSku)) {
      setEditing(editSku);
      setShowCreate(false);
    }
  }, [products]);


  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
            {t("admin.products.title")}
          </h1>
          <p className="mt-1 text-sm text-fg-tertiary">
            {t("admin.products.itemsInCatalog", { count: products.length })} ·{" "}
            {t("admin.products.pendingCount", { count: products.filter((p) => p.approved === "pending").length })} ·{" "}
            {t("admin.products.nonLiveCount", { count: products.filter((p) => p.approved !== "approved").length })}
          </p>
        </div>
        <Button onClick={() => setShowCreate((s) => !s)}>
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          {t("admin.products.newProduct")}
        </Button>
      </header>

      {showCreate && (
        <Card>
          <CardContent className="p-6">
            <ProductForm
              onCancel={() => setShowCreate(false)}
              onSubmit={(draft) => {
                setProducts((prev) => [draft, ...prev]);
                setShowCreate(false);
              }}
            />
          </CardContent>
        </Card>
      )}

      {products.length === 0 ? (
        <EmptyState
          title={t("admin.products.emptyTitle")}
          description={t("admin.products.emptyDescription")}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {products.map((p) => (
                <ProductRow
                  key={p.sku}
                  product={p}
                  isEditing={editing === p.sku}
                  onEdit={() => setEditing(p.sku)}
                  onCancel={() => setEditing(null)}
                  onSave={(updated) => {
                    setProducts((prev) => prev.map((x) => (x.sku === updated.sku ? updated : x)));
                    setEditing(null);
                  }}
                  onDelete={() =>
                    setProducts((prev) => prev.filter((x) => x.sku !== p.sku))
                  }
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface ProductRowProps {
  product: Product;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (p: Product) => void;
  onDelete: () => void;
}
function ProductRow({
  product,
  isEditing,
  onEdit,
  onCancel,
  onSave,
  onDelete,
}: ProductRowProps) {
  const { t } = useTranslation();

  if (isEditing) {
    return (
      <div className="px-5 py-4">
        <ProductForm initial={product} onCancel={onCancel} onSubmit={onSave} />
      </div>
    );
  }

  const gallerySize = product.images?.length ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-3 px-5 py-4">
      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-border bg-bg-elevated">
        {product.image && product.image !== "x" ? (
          <img src={product.image} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-fg-quaternary">
            <ImageIcon className="h-5 w-5" strokeWidth={1.5} />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-[10px] text-fg-secondary">
            {product.sku}
          </code>
          {product.inStock ? (
            <Badge variant="success" size="sm">{t("admin.products.inStock")}</Badge>
          ) : (
            <Badge variant="default" size="sm">{t("admin.products.outOfStock")}</Badge>
          )}
          {product.featured && <Badge variant="teal" size="sm">{t("admin.products.featured")}</Badge>}
          <Badge variant={product.approved === "approved" ? "success" : product.approved === "pending" ? "warning" : "danger"} size="sm">
            {t(APPROVED_STATUS_LABEL_KEYS[product.approved])}
          </Badge>
          <span className="rounded bg-bg-elevated px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-fg-tertiary">
            {product.category}
          </span>
          <span className="text-[10px] text-fg-quaternary">{product.brand}</span>
          {gallerySize > 0 && (
            <span className="flex items-center gap-1 text-[10px] text-fg-quaternary" title={t("admin.products.galleryImages", { count: gallerySize })}>
              <ImageIcon className="h-3 w-3" strokeWidth={1.5} />
              {gallerySize + 1}
            </span>
          )}
        </div>
        <div className="mt-1 truncate text-sm font-510 text-fg-primary">
          {product.name}
        </div>
      </div>
      <div className="text-sm font-510 text-fg-primary">
        {formatPrice(product.priceEUR, "EUR")}
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Pencil className="h-3 w-3" strokeWidth={1.5} />
          {t("admin.products.edit")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          aria-label={t("admin.products.delete", { name: product.name })}
        >
          <Trash2 className="h-3 w-3 text-accent-rose" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
}

interface ProductFormProps {
  initial?: Product;
  onCancel: () => void;
  onSubmit: (p: Product) => void;
}
function ProductForm({ initial, onCancel, onSubmit }: ProductFormProps) {
  const { t } = useTranslation();
  const [sku, setSku] = useState(initial?.sku ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [brand, setBrand] = useState(initial?.brand ?? "");
  const [category, setCategory] = useState<Category>(initial?.category ?? "video");
  const [priceEUR, setPriceEUR] = useState<number>(initial?.priceEUR ?? 0);
  const [description, setDescription] = useState(initial?.description ?? "");
  const [tagsInput, setTagsInput] = useState((initial?.tags ?? []).join(", "));
  const [notes, setNotes] = useState<string>(initial?.vendorNotes ?? "");
  const [inStock, setInStock] = useState(initial?.inStock ?? true);
  const [featured, setFeatured] = useState(initial?.featured ?? false);
  const [approved, setApproved] = useState<Product["approved"]>(initial?.approved ?? "pending");

  // Image gallery
  const [image, setImage] = useState<string>(initial?.image ?? "");
  const [gallery, setGallery] = useState<string[]>(initial?.images ?? []);
  const [newImage, setNewImage] = useState("");

  const isValid = !!sku.trim() && !!name.trim() && !!brand.trim() && priceEUR > 0 && image.trim().length > 0;

  function addImage() {
    const v = newImage.trim();
    if (!v) return;
    setGallery((g) => [...g, v]);
    setNewImage("");
  }
  function removeImage(idx: number) {
    setGallery((g) => g.filter((_, i) => i !== idx));
  }
  function moveImageUp(idx: number) {
    if (idx === 0) return;
    setGallery((g) => {
      const next = [...g];
      const a = next[idx - 1];
      const b = next[idx];
      if (a !== undefined && b !== undefined) {
        next[idx - 1] = b;
        next[idx] = a;
      }
      return next;
    });
  }
  function moveImageDown(idx: number) {
    if (idx === gallery.length - 1) return;
    setGallery((g) => {
      const next = [...g];
      const a = next[idx];
      const b = next[idx + 1];
      if (a !== undefined && b !== undefined) {
        next[idx] = b;
        next[idx + 1] = a;
      }
      return next;
    });
  }
  function makeMainImageFromGallery(idx: number) {
    const promoted = gallery[idx];
    if (typeof promoted !== "string") return;
    const currentMain = image;
    const rest = gallery.filter((_, i) => i !== idx);
    setGallery(currentMain ? [currentMain, ...rest] : rest);
    setImage(promoted);
  }

  const submit = () => {
    if (!isValid) return;
    onSubmit({
      sku: sku.trim(),
      name: name.trim(),
      brand: brand.trim(),
      category,
      description: description.trim(),
      priceEUR,
      currency: "EUR",
      image: image.trim(),
      images: gallery,
      specs: initial?.specs ?? [],
      tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      inStock,
      featured,
      updatedAt: new Date().toISOString(),
      approved,
      vendorNotes: notes.trim() || undefined,
    });
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardContent className="space-y-4 p-5">
          <header>
            <h3 className="flex items-center gap-2 text-xs font-510 uppercase tracking-wider text-fg-tertiary">
              <ImageIcon className="h-3 w-3" strokeWidth={1.5} />
              {t("admin.products.mainHeroImage")}
            </h3>
          </header>
          <Field label={t("admin.products.imagePathOrUrl")} hint={t("admin.products.imagePathHint")}>
            <div className="flex gap-2">
              <Input
                value={image}
                onChange={(e) => setImage(e.target.value)}
                placeholder={t("admin.products.imagePlaceholder")}
                className="flex-1"
              />
              {image && (
                <div className="h-9 w-16 overflow-hidden rounded-md border border-border">
                  <img src={image} alt="" className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                </div>
              )}
            </div>
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-5">
          <header className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-xs font-510 uppercase tracking-wider text-fg-tertiary">
              <ImageIcon className="h-3 w-3" strokeWidth={1.5} />
              {t("admin.products.galleryTitle")}
            </h3>
            <span className="text-[10px] text-fg-quaternary">
              {t("admin.products.galleryCountShown", { count: gallery.length })}
            </span>
          </header>

          {gallery.length > 0 && (
            <ul className="divide-y divide-border/50 rounded-md border border-border bg-bg-canvas">
              {gallery.map((img, i) => (
                <li key={img + i} className="flex items-center gap-2 px-3 py-2">
                  <GripVertical className="h-3 w-3 text-fg-quaternary" strokeWidth={1.5} />
                  <div className="h-8 w-10 flex-shrink-0 overflow-hidden rounded border border-border">
                    <img src={img} alt="" className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                  </div>
                  <code className="flex-1 truncate font-mono text-[11px] text-fg-secondary">{img}</code>
                  <button
                    type="button"
                    onClick={() => makeMainImageFromGallery(i)}
                    title={t("admin.products.promoteToMain")}
                    className="rounded border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-fg-tertiary hover:bg-bg-elevated hover:text-benchmark-teal-bright"
                  >
                    {t("admin.products.makeMain")}
                  </button>
                  <button
                    type="button"
                    onClick={() => moveImageUp(i)}
                    disabled={i === 0}
                    aria-label={t("admin.products.moveUp")}
                    className="rounded p-1 text-fg-tertiary hover:bg-bg-elevated hover:text-fg-primary disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveImageDown(i)}
                    disabled={i === gallery.length - 1}
                    aria-label={t("admin.products.moveDown")}
                    className="rounded p-1 text-fg-tertiary hover:bg-bg-elevated hover:text-fg-primary disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    aria-label={t("admin.products.remove")}
                    className="rounded p-1 text-accent-rose hover:bg-accent-rose/10"
                  >
                    <X className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-2">
            <Input
              value={newImage}
              onChange={(e) => setNewImage(e.target.value)}
              placeholder={t("admin.products.galleryPlaceholder")}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addImage();
                }
              }}
              className="flex-1"
            />
            <Button size="sm" variant="secondary" onClick={addImage} disabled={!newImage.trim()}>
              {t("admin.products.addImage")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("admin.products.sku")}>
              <Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder={t("admin.products.skuPlaceholder")} disabled={!!initial} />
            </Field>
            <Field label={t("admin.products.brand")}>
              <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder={t("admin.products.brandPlaceholder")} />
            </Field>
          </div>
          <Field label={t("admin.products.name")}>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("admin.products.namePlaceholder")} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t("admin.products.category")}>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as Category)}
                className="h-9 w-full rounded border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary focus:border-accent-indigo focus:outline-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label={t("admin.products.priceEur")}>
              <Input
                type="number"
                min={0}
                value={priceEUR}
                onChange={(e) => setPriceEUR(Number(e.target.value))}
              />
            </Field>
            <Field label={t("admin.products.approvalStatus")}>
              <select
                value={approved}
                onChange={(e) => setApproved(e.target.value as Product["approved"])}
                className="h-9 w-full rounded border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary focus:border-accent-indigo focus:outline-none"
              >
                {APPROVED_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label={t("admin.products.description")}>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder={t("admin.products.descriptionPlaceholder")}
              className={cn(
                "w-full rounded border border-border bg-bg-surface px-3 py-1.5",
                "text-sm text-fg-primary placeholder:text-fg-tertiary",
                "transition-colors focus:border-accent-indigo focus:outline-none",
              )}
            />
          </Field>
          <Field label={t("admin.products.tags")}>
            <Input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder={t("admin.products.tagsPlaceholder")}
            />
          </Field>
          <Field label={t("admin.products.vendorNotes")} hint={t("admin.products.vendorNotesHint")}>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={t("admin.products.vendorNotesPlaceholder")}
              className="w-full rounded border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary placeholder:text-fg-tertiary"
            />
          </Field>
          <div className="flex flex-wrap items-center gap-4">
            <Toggle checked={inStock} onChange={setInStock} label={t("admin.products.inStock")} />
            <Toggle checked={featured} onChange={setFeatured} label={t("admin.products.featured")} />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {t("admin.products.cancel")}
        </Button>
        <Button size="sm" disabled={!isValid} onClick={submit}>
          <Check className="h-3 w-3" strokeWidth={1.5} />
          {initial
            ? t("admin.products.saveChanges")
            : approved === "approved"
              ? t("admin.products.createPublish")
              : t("admin.products.saveDraft")}
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-baseline justify-between text-xs font-510 uppercase tracking-wider text-fg-tertiary">
        <span>{label}</span>
        {hint && <span className="ml-2 text-[10px] font-normal normal-case text-fg-quaternary">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      className="flex items-center gap-2 text-sm"
    >
      <span
        className={cn(
          "flex h-5 w-9 items-center rounded-full transition-colors",
          checked ? "bg-accent-green/30" : "bg-bg-elevated",
        )}
      >
        <span
          className={cn(
            "h-4 w-4 rounded-full bg-white shadow-sm transition-transform",
            checked ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </span>
      <span className="text-fg-secondary">{label}</span>
    </button>
  );
}
